/*
 * sr-add-to-bag.js (v3, 2026-09-02)
 * ---------------------------------------------------------------------
 * Drives the shared "Add to Bag" modal (snippets/sr-add-to-bag-modal.liquid)
 * for saree product cards: Falls & Edging / Gift Wrap selection -> real
 * /cart/add.js writes -> in-place success confirmation.
 *
 * Trigger buttons carry:
 *   data-atb-trigger
 *   data-atb-product-id
 *   data-atb-variant-id
 *   data-atb-product-title
 *   data-atb-product-image   (optional)
 *
 * Deliberately self-contained - does not call into, modify, or share DOM
 * with snippets/native-customization.liquid. See the modal snippet's own
 * header comment for the full rationale.
 *
 * v2 FIX (2026-09-02, caught in live testing): the header bag count never
 * updated after an add. See refreshCartCount().
 *
 * v3 PERF (2026-09-02): the add took 5-9 seconds. Measured live on a product
 * page, the four BLOCKING calls were:
 *     /cart.js       689 ms   read the cart to find an existing saree line
 *     /cart/add.js  1366 ms   add the saree
 *     /cart.js      1457 ms   re-read the cart to find stale service lines
 *     /cart/add.js  1889 ms   add the service
 *                  ------
 *                  ~5400 ms
 * (The store's analytics beacons - /events, /v1/metrics, /g/collect - are
 * competing for connections throughout, which is why each call is slower than
 * a quiet Shopify store would be. That is not something this file can fix.)
 *
 * Two of those four are removable, and both are removed here:
 *   1. THE SECOND /cart.js IS REDUNDANT. It existed to spot stale service
 *      lines sharing our customization id. But stale lines can only exist
 *      when we REUSED an id off an existing saree line - and in that case
 *      they are already present in the FIRST cart snapshot. The only write
 *      between the two reads is an optional properties tag on the parent
 *      line, which cannot create or remove a service line. When we mint a
 *      fresh id there is nothing to be stale by definition.
 *   2. THE TWO ADDS ARE ONE ADD. /cart/add.js takes an items[] array, so the
 *      saree and its services go in a single request instead of two
 *      sequential ones.
 *
 * Result: ~5.4 s of blocking work becomes ~2.6 s - one /cart.js, one
 * /cart/add.js. The count refresh afterwards is deliberately NOT awaited, so
 * it never delays the confirmation the customer is waiting for.
 *
 * ONE BEHAVIOUR CHANGE WORTH KNOWING: batching makes the add atomic. Before,
 * a failure adding the service left the saree already in the bag alongside an
 * error message - a half-done state the customer had to untangle. Now either
 * both land or neither does, and the error means exactly what it says.
 */
(function () {
  if (window.__srAddToBagInstalled) return;
  window.__srAddToBagInstalled = true;

  var modal, fallsSelect, giftwrapCheckbox, statusEl, continueBtn, titleEl, imageEl;
  var currentVariantId = null;
  var busy = false;

  function categoryOf(serviceType) {
    return serviceType ? String(serviceType).split('_')[0] : null;
  }

  function mintCustomizationId() {
    return 'cid_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
  }

  function setStatus(text) {
    if (!statusEl) return;
    statusEl.hidden = !text;
    statusEl.textContent = text || '';
  }

  function openModal(trigger) {
    currentVariantId = trigger.getAttribute('data-atb-variant-id');
    var title = trigger.getAttribute('data-atb-product-title') || '';
    var image = trigger.getAttribute('data-atb-product-image') || '';

    if (titleEl) titleEl.textContent = title;
    if (imageEl) {
      if (image) {
        imageEl.src = image;
        imageEl.hidden = false;
      } else {
        imageEl.hidden = true;
      }
    }

    hideSuccess();
    if (fallsSelect) fallsSelect.value = '';
    if (giftwrapCheckbox) giftwrapCheckbox.checked = false;
    setStatus('');
    if (continueBtn) {
      continueBtn.disabled = false;
      continueBtn.textContent = 'Continue / Add to Cart';
    }

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sr-atb-modal-open');
  }

  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sr-atb-modal-open');
  }

  /*
   * FIX (2026-09-01, reported from live): after a successful add the modal
   * closed and nothing else visibly happened - the customer was returned to
   * the page they were on with no confirmation, so it read as if the add had
   * failed or bounced them home.
   *
   * The old refreshAndOpenCartDrawer() did two things, and the first broke the
   * second: it fetched fresh cart-drawer markup and called
   *     oldDrawer.replaceWith(freshDrawer)
   * which swaps out the LIVE <cart-drawer> custom element for an un-upgraded
   * copy - destroying the element's upgraded instance and its listeners. The
   * following click on a[aria-controls="cart-drawer"] then had nothing left to
   * intercept it, so the drawer never opened.
   *
   * Rather than reach further into the theme's internals, the modal now
   * confirms in place (showSuccess below) and only refreshes the header count.
   * Nothing is replaced, so no custom element is ever destroyed.
   *
   * Deliberately NOT awaited by the caller: the customer's confirmation must
   * not wait on a cosmetic badge.
   */
  async function refreshCartCount() {
    try {
      var res = await fetch('/cart.js');
      if (!res.ok) return;
      var cart = await res.json();

      /*
       * v2 FIX - note the bare `cart-count` TAG at the front of this list.
       *
       * This theme (Impact) renders the header badge as a <cart-count> CUSTOM
       * ELEMENT wrapped in a <span class="header__cart-count">. Before this
       * fix the selector list matched only attributes and classes, so:
       *   - <cart-count> itself matched nothing (a tag is not a class), and
       *   - .header__cart-count DID match [class*="cart-count"], but it is a
       *     wrapper with a child element, so the leaf guard below correctly
       *     skipped it.
       * Net result: nothing was ever updated and the badge sat on its old
       * number until the next page load. Verified live 2026-09-02 - cart had
       * 2 items, badge still read 1.
       *
       * The leaf guard stays: it is what stops us overwriting a wrapper and
       * destroying the custom element inside it.
       */
      var nodes = document.querySelectorAll('cart-count, [data-cart-count], .cart-count, [class*="cart-count"]');
      Array.prototype.forEach.call(nodes, function (n) {
        // Only leaf nodes - never overwrite a wrapper that contains markup.
        if (n.children.length === 0) n.textContent = String(cart.item_count);
      });
    } catch (e) {
      // A stale count is cosmetic and corrects itself on the next page load;
      // it must never surface as an error over a successful add.
    }
  }

  function serviceLabel(svc) {
    var variants = window.SR_ATB_VARIANTS || {};
    if (svc.service_type === 'gift_wrap') return 'Gift wrap';
    if (svc.id === variants.fallsMachine) return 'Falls & edging — machine stitched';
    return 'Falls & edging — hand stitched';
  }

  function hideSuccess() {
    if (!modal) return;
    var form = modal.querySelector('[data-atb-form]');
    var success = modal.querySelector('[data-atb-success]');
    if (form) form.hidden = false;
    if (success) success.hidden = true;
  }

  function showSuccess(selections) {
    var form = modal.querySelector('[data-atb-form]');
    var success = modal.querySelector('[data-atb-success]');
    var list = modal.querySelector('[data-atb-success-list]');

    // If the success markup is missing for any reason, fall back to the old
    // behaviour rather than leaving the customer staring at a live form.
    if (!success) { closeModal(); return; }

    if (list) {
      list.innerHTML = '';
      var li = document.createElement('li');
      li.textContent = titleEl && titleEl.textContent ? titleEl.textContent : 'Saree';
      list.appendChild(li);
      (selections || []).forEach(function (svc) {
        var item = document.createElement('li');
        item.textContent = serviceLabel(svc);
        list.appendChild(item);
      });
    }

    if (form) form.hidden = true;
    success.hidden = false;
    var focusTarget = success.querySelector('button, a');
    if (focusTarget) focusTarget.focus();
  }

  async function onContinue() {
    if (busy || !currentVariantId) return;
    busy = true;
    continueBtn.disabled = true;
    setStatus('Adding to your bag…');

    var variantId = parseInt(currentVariantId, 10);
    var variants = window.SR_ATB_VARIANTS || {};
    var succeeded = false;

    var selections = [];
    if (fallsSelect && fallsSelect.value) {
      var fallsId = parseInt(fallsSelect.value, 10);
      selections.push({
        id: fallsId,
        service_type: fallsId === variants.fallsMachine ? 'falls_machine' : 'falls_hand'
      });
    }
    if (giftwrapCheckbox && giftwrapCheckbox.checked) {
      selections.push({ id: parseInt(giftwrapCheckbox.value, 10), service_type: 'gift_wrap' });
    }

    try {
      // BLOCKING CALL 1 of 2. Needed: without knowing whether this saree is
      // already in the bag we would create a second line for it, because our
      // freshly minted _customization_id makes the line look different to
      // Shopify's own line-merging.
      var cartRes = await fetch('/cart.js');
      if (!cartRes.ok) throw new Error('Could not read current cart');
      var cart = await cartRes.json();

      var existingParent = null;
      for (var i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        if (it.variant_id === variantId && !(it.properties && it.properties['_service_type'])) {
          existingParent = it;
          break;
        }
      }

      var customizationId;
      var reusedExistingId = false;

      if (existingParent) {
        customizationId = (existingParent.properties && existingParent.properties['_customization_id']) || null;
        if (customizationId) {
          reusedExistingId = true;
        } else {
          customizationId = mintCustomizationId();
          var mergedProps = {};
          var existingProps = existingParent.properties || {};
          for (var k in existingProps) {
            if (Object.prototype.hasOwnProperty.call(existingProps, k)) mergedProps[k] = existingProps[k];
          }
          mergedProps['_customization_id'] = customizationId;

          var tagRes = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: existingParent.key, quantity: existingParent.quantity, properties: mergedProps })
          });
          if (!tagRes.ok) throw new Error('Could not tag the existing saree line');
        }
      } else {
        customizationId = mintCustomizationId();
      }

      var qty = existingParent ? existingParent.quantity : 1;

      /*
       * v3 - stale service lines are only POSSIBLE when we reused an id that
       * was already on a saree line. A freshly minted id cannot have anything
       * attached to it yet, and the tag-the-parent branch above mints one too.
       * So this whole scan (and the second /cart.js it used to require) is
       * skipped entirely for the common "new saree" path.
       *
       * `cart` is the first snapshot, which is correct here: the only write
       * since it was taken is the properties tag above, and that cannot add or
       * remove a service line.
       */
      if (reusedExistingId && selections.length > 0) {
        var newCategories = selections.map(function (s) { return categoryOf(s.service_type); });
        var stale = cart.items.filter(function (item) {
          var p = item.properties || {};
          if (p['_customization_id'] !== customizationId) return false;
          var cat = categoryOf(p['_service_type']);
          return cat && newCategories.indexOf(cat) !== -1;
        });
        for (var s = 0; s < stale.length; s++) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: stale[s].key, quantity: 0 })
          });
        }
      }

      /*
       * BLOCKING CALL 2 of 2. The saree and every selected service go up in a
       * SINGLE /cart/add.js. This is the change that took the flow from four
       * sequential requests to two.
       */
      var itemsToAdd = [];
      if (!existingParent) {
        itemsToAdd.push({
          id: variantId,
          quantity: 1,
          properties: { '_customization_id': customizationId }
        });
      }
      selections.forEach(function (svc) {
        itemsToAdd.push({
          id: svc.id,
          quantity: qty,
          properties: { '_customization_id': customizationId, '_service_type': svc.service_type }
        });
      });

      if (itemsToAdd.length > 0) {
        var addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: itemsToAdd })
        });
        if (!addRes.ok) throw new Error('Could not add this to your bag');
      }

      setStatus('');
      succeeded = true;
      showSuccess(selections);
      refreshCartCount(); // intentionally not awaited
    } catch (err) {
      setStatus('Something went wrong adding this to your bag. Please try again.');
      console.error('[sr-add-to-bag]', err);
    } finally {
      busy = false;
      // v3 - only re-enable on FAILURE. On success the form is hidden behind
      // the confirmation, and leaving a live button under it is an invitation
      // to add the same saree twice.
      if (continueBtn && !succeeded) continueBtn.disabled = false;
    }
  }

  function init() {
    modal = document.getElementById('sr-atb-modal');
    if (!modal) return;

    fallsSelect = modal.querySelector('[data-atb-falls]');
    giftwrapCheckbox = modal.querySelector('[data-atb-giftwrap]');
    statusEl = modal.querySelector('[data-atb-status]');
    continueBtn = modal.querySelector('[data-atb-continue]');
    titleEl = modal.querySelector('[data-atb-title]');
    imageEl = modal.querySelector('[data-atb-image]');

    if (continueBtn) continueBtn.addEventListener('click', onContinue);

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var trigger = target.closest('[data-atb-trigger]');
      if (trigger) {
        event.preventDefault();
        openModal(trigger);
        return;
      }

      if (target.closest('[data-atb-view-cart]')) {
        window.location.href = '/cart';
        return;
      }

      if (target.closest('[data-atb-close]')) {
        closeModal();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && modal && !modal.hidden) closeModal();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();